import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    actions,
    formatColumnTitle,
    getPermissionModuleId,
    SPECIAL_PERMISSIONS,
    WEBSITE_ROLES_MODEL,
} from '../Model/AllowedModules';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import { CustomCheckbox } from '../../../../../components/CustomCheckbox';
import { ACTION_MODEL } from '../../../../../Helpers/ActionModel';
import {
    IconCheckbox,
    IconCheckboxChecked,
    IconCheckboxUnchecked,
} from '../../../../../components/buttons/IconButtons';
import CustomTooltip from '../../../../../components/CustomTooltip';
import { countSelectedPermissions, handleSelectAllPermissions, isAllPermissionsSelected } from './CommonsPermissions';

/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend - IBL
 * @Email: sgarcia@ibl.mx
 */

const WebsiteRolesApp = ({ web = [], selectedPermissions = [], setSelectedPermissions, readOnly }) => {
    const { t } = useTranslation();
    const [selectedInnerView, setSelectedInnerView] = useState(WEBSITE_ROLES_MODEL[0]?.id);
    const tableId = 'tbl_wra_table';

    const handleStepSelectedInnerView = (tab) => {
        setSelectedInnerView(tab);
    };

    const getAction = (permissionName = '') => {
        const action = permissionName.trim().split('_').pop()?.toLowerCase();

        return action === ACTION_MODEL.VIEW.toLowerCase() ? ACTION_MODEL.READ.toLowerCase() : action;
    };

    const availableRoles = WEBSITE_ROLES_MODEL;

    const selectedRole = availableRoles.find((item) => item.id === selectedInnerView);
    // Tabs are listed alphabetically by their translated name
    const roles = [...availableRoles].sort((a, b) => t(a.translation).localeCompare(t(b.translation)));
    /**
     * Permissions one tab holds: the ones of the module and the ones of its
     * children, each returned by the backend under its own module id.
     *
     * @param {Object} role - Entry of WEBSITE_ROLES_MODEL.
     * @returns {Array} Permissions of that tab.
     */
    const getRolePermissions = (role) =>
        (web || []).filter((permission) => {
            const moduleId = getPermissionModuleId(permission);

            return moduleId === role?.id || role?.children?.some((child) => child.id === moduleId);
        });

    const permissions = getRolePermissions(selectedRole);
    const standardColumns = actions.filter((action) => {
        return permissions.some((permission) => {
            const permAction = getAction(permission.name);
            return permAction === action.toLowerCase();
        });
    });

    const customColumns = [
        ...new Set(
            permissions
                .filter(
                    (permission) =>
                        SPECIAL_PERMISSIONS.includes(permission.name) || !actions.includes(getAction(permission.name)),
                )
                .map((permission) => permission.name),
        ),
    ];

    const columns = [...new Set([...standardColumns, ...customColumns])].filter(c => c && c.trim());

    const thead = [
        <CustomTh tableId={tableId} key={'module'}>
            {''}
        </CustomTh>,

        ...columns.map((column) => (
            <CustomTh tableId={tableId} key={column}>
                <CustomTooltip text={formatColumnTitle(t(column))}>
                    <div className='text-center text-capitalize'>{formatColumnTitle(t(column))}</div>
                </CustomTooltip>
            </CustomTh>
        )),
    ];

    /**
     * Resolves the permission a cell renders, so the select all checkbox and the row
     * share a single rule and cannot disagree.
     *
     * @param {Array} childPermissions - Permissions belonging to the row module.
     * @param {string} column - Column key: an action or a full permission name.
     * @returns {Object|undefined} The permission shown in that cell, if any.
     */
    const resolveCellPermission = (childPermissions, column) =>
        childPermissions.find((permission) => {
            if (actions.includes(column) && !SPECIAL_PERMISSIONS.includes(permission.name)) {
                // The action is resolved with the same rule as the columns, so the
                // modules that name their read permission _VIEW also match it.
                return getAction(permission.name) === column.toLowerCase();
            }

            return permission.name === column;
        });

    const handleCheckboxChange = (permissionId, checked) => {
        setSelectedPermissions((prev = []) => {
            if (checked) {
                const exists = prev.some((item) => item.id === permissionId);
                if (exists) return prev;
                return [...prev, { id: permissionId }];
            }
            return prev.filter((item) => item.id !== permissionId);
        });
    };

    const tbody = {
        body: (selectedRole?.children || []).map((child) => {
            const childPermissions = permissions.filter((permission) => getPermissionModuleId(permission) === child.id);

            return (
                <>
                    <CustomTd tableId={tableId}>
                        <p className='fs-12px font-color-text-950 ibl-font-w-600'>{t(child.translation)}</p>
                    </CustomTd>

                    {columns.map((column) => {
                        const permission = resolveCellPermission(childPermissions, column);
                        return (
                            <CustomTd tableId={tableId} key={`${child.id}-${column}`}>
                                <div className='d-flex justify-content-center align-items-center'>
                                    <CustomCheckbox
                                        disabled={readOnly}
                                        icon={
                                            !permission ? (
                                                <IconCheckbox />
                                            ) : (
                                                <IconCheckboxUnchecked className='icon-color-general-950' />
                                            )
                                        }
                                        checkedIcon={
                                            !permission ? (
                                                <IconCheckbox />
                                            ) : (
                                                <IconCheckboxChecked className='icon-color-shades-800' />
                                            )
                                        }
                                        checked={
                                            permission
                                                ? selectedPermissions.some((item) => item.id === permission?.id)
                                                : false
                                        }
                                        onChange={(e) =>
                                            permission && handleCheckboxChange(permission.id, e.target.checked)
                                        }
                                    />
                                </div>
                            </CustomTd>
                        );
                    })}
                </>
            );
        }),

        data: selectedRole?.children || [],
    };

    /**
     * Permissions of one tab the role has selected, painted on its badge and on
     * the card of the module.
     *
     * @param {Object} role - Entry of WEBSITE_ROLES_MODEL.
     * @returns {number} Selected permissions of that tab.
     */
    const getSelectedCount = (role) => countSelectedPermissions(getRolePermissions(role), selectedPermissions);

    const selectedRoleCount = getSelectedCount(selectedRole);

    // Only the permissions the table renders as a checkbox can be toggled, so the select all
    // checkbox is computed over those and not over every permission of the module
    const currentPermissions = [
        ...new Map(
            (selectedRole?.children || [])
                .flatMap((child) => {
                    const childPermissions = permissions.filter(
                        (permission) => getPermissionModuleId(permission) === child.id,
                    );

                    return columns.map((column) => resolveCellPermission(childPermissions, column));
                })
                .filter((permission) => permission?.id)
                .map((permission) => [permission.id, permission]),
        ).values(),
    ];

    const isAllChecked = isAllPermissionsSelected(currentPermissions, selectedPermissions);

    const handleSelectAll = (checked) => {
        handleSelectAllPermissions(checked, currentPermissions, setSelectedPermissions);
    };



    return {
        t,
        roles,
        selectedInnerView,
        handleStepSelectedInnerView,
        selectedRole,
        thead,
        tbody,
        selectedRoleCount,
        getSelectedCount,
        isAllChecked,
        handleSelectAll
    };
};

export default WebsiteRolesApp;
